import type { DraftResourceType } from "src/components/drafts/types";
import { useEffect, useRef, useState } from "react";

import {
  Button,
  ChakraModal,
  ChakraModalBody,
  ChakraModalContent,
  ChakraModalFooter,
  ChakraModalHeader,
  ChakraModalOverlay,
  Paragraph,
  Text,
  useToast,
} from "@hightouch/htui";
import { REASON_CODE_MESSAGES } from "@hightouch/lib/query/column-validation";
import { captureException } from "src/lib/monitoring";

import {
  DraftSubmissionForm,
  publishNow,
  saveAndRequestApproval,
  saveOnly,
} from "src/components/drafts/draft-submission-form";
import type { ApproverOptions } from "src/components/drafts/format-reviewers-input";
import { DeleteConfirmationModal } from "src/components/modals/delete-confirmation-modal";
import { useDraft } from "src/contexts/draft-context";
import {
  type CreateModelConfigurationInput,
  DraftOperation,
  DraftStatus,
  type ModelDraft,
  type SyncDraft,
  useCreateModelConfigurationMutation,
  useDraftModelConfigurationMutation,
  useDraftsQuery,
  useSubmitDraftModelMutation,
  useSubmitDraftSyncMutation,
  useUpdateDraftsStatusMutation,
} from "src/graphql";
import * as analytics from "src/lib/analytics";
import { invalidateQueries } from "src/lib/react-query";
import {
  type ResourcePermissionInput,
  useResourcePermission,
} from "src/components/permission/use-resource-permission";

const formatBlockedColumnsError = (
  errors: Array<{ column_name: string; reasons: readonly string[] }>,
): string => {
  const details = errors
    .map((e) => {
      const reasons = e.reasons.map((r) => REASON_CODE_MESSAGES[r] ?? r);
      return reasons.length
        ? `${e.column_name} (${reasons.join("; ")})`
        : e.column_name;
    })
    .join(", ");
  return `These column changes can't be saved: ${details}`;
};

interface DraftSubmissionProps<Resource extends "sync" | "model"> {
  getResourceId: () => string | Promise<string>;
  resource: Resource;
  operation: DraftOperation;
  draft: SyncDraft | ModelDraft | undefined;
  permission: ResourcePermissionInput<Resource>;
  open: boolean;
  onClose: () => void;
  onSubmit: (
    resourceId: string,
    { publishNow }: { publishNow: boolean },
  ) => void;
  dependentResource?: {
    resourceId: string;
    resourceType: DraftResourceType;
  };
  approverOptions: ApproverOptions;
  invalidateQueriesOnSubmit?: boolean;
  getCreateModelInput?: () => Promise<
    Omit<CreateModelConfigurationInput, "draft">
  >;
}

export function DraftSubmissionModal<Resource extends "sync" | "model">({
  getResourceId,
  resource,
  operation,
  open,
  onClose,
  onSubmit,
  draft,
  dependentResource,
  permission,
  approverOptions,
  invalidateQueriesOnSubmit = false,
  getCreateModelInput,
}: DraftSubmissionProps<Resource>) {
  const { isPermitted: canApprove } = useResourcePermission(permission);

  const [comment, setComment] = useState<string>("");
  const { toast } = useToast();
  const {
    draftChange,
    editingDraft,
    setEditingDraft,
    draft: existingDraft,
  } = useDraft();
  const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
  const [error, setError] = useState("");

  // The modelId comes from createModelConfiguration when creating via that path
  const createdModelIdRef = useRef<string | null>(null);
  const { mutateAsync: draftModelConfiguration } =
    useDraftModelConfigurationMutation();
  const { mutateAsync: createModelConfiguration } =
    useCreateModelConfigurationMutation();
  const { mutateAsync: submitSync } = useSubmitDraftSyncMutation(
    invalidateQueriesOnSubmit
      ? undefined
      : {
          onSuccess: () => {
            // Prevent refetching queries
          },
        },
  );
  const { mutateAsync: submitModel } = useSubmitDraftModelMutation(
    invalidateQueriesOnSubmit
      ? undefined
      : {
          onSuccess: () => {
            // Prevent refetching queries
          },
        },
  );
  const { mutateAsync: updateDraftStatus } = useUpdateDraftsStatusMutation({
    onSuccess: () => {
      // Suppress the global invalidation so `handleSave` can invalidate
      // explicitly and await the refetch before leaving draft mode.
    },
  });
  const [selectedRadioOption, setSelectedRadioOption] = useState<string>(
    canApprove ? publishNow : saveAndRequestApproval,
  );
  const { data: dependentDraft } = useDraftsQuery(
    {
      resourceId: dependentResource?.resourceId.toString() ?? "",
      resourceType: dependentResource?.resourceType ?? "",
      status: "pending",
    },
    {
      enabled:
        Boolean(dependentResource?.resourceId) &&
        Boolean(dependentResource?.resourceType),
      select: (data) => data?.drafts?.[0],
    },
  );
  const extraDraftIds = dependentDraft ? [dependentDraft.id] : [];

  const [saveLoading, setSaveLoading] = useState(false);

  useEffect(() => {
    const alreadyRequestedFrom =
      (existingDraft?.approval_requests.map(
        (request) => request.user_id,
      ) as number[]) || [];
    setSelectedUsers(alreadyRequestedFrom);
  }, [existingDraft]);

  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  useEffect(() => {
    if (!open) {
      setComment("");
      setSelectedRadioOption(canApprove ? publishNow : saveAndRequestApproval);
    }
  }, [open]);

  const saveResource = async (resourceId: string) => {
    const draftInput = {
      resourceId: String(resourceId),
      comment,
      approverIds:
        selectedRadioOption === saveAndRequestApproval ? selectedUsers : [],
      operation,
    };
    try {
      if (resource === "sync") {
        const res = await submitSync({
          ...draftInput,
          draft: draft as SyncDraft,
        });
        return res.submitDraftSync.id;
      } else if (resource === "model") {
        if (operation === DraftOperation.Create && getCreateModelInput) {
          const res = await createModelConfiguration({
            input: { ...(await getCreateModelInput()), draft: true },
            approverIds:
              selectedRadioOption === saveAndRequestApproval
                ? selectedUsers
                : [],
            comment,
          });
          const errors = res.createModelConfiguration.errors;
          if (errors && errors.length > 0) {
            throw new Error(formatBlockedColumnsError(errors));
          }
          createdModelIdRef.current = res.createModelConfiguration.model_id
            ? String(res.createModelConfiguration.model_id)
            : null;
          return res.createModelConfiguration.draft_id ?? undefined;
        }

        const modelDraft = draft as ModelDraft | undefined;
        const draftSet = modelDraft?._set ?? existingDraft?.new_resource?._set;

        if (draftSet?.query_type) {
          // only need to call draftModelConfiguration if the query config changes
          const res = await draftModelConfiguration({
            modelId: String(resourceId),
            input: {
              query_type: String(draftSet.query_type),
              query_raw_sql: draftSet.query_raw_sql as string | undefined,
              query_table_name: draftSet.query_table_name as string | undefined,
              query_dbt_model_id: draftSet.query_dbt_model_id as
                | number
                | undefined,
              query_looker_look_id: draftSet.query_looker_look_id as
                | string
                | undefined,
              query_integrations: draftSet.query_integrations,
              custom_query: draftSet.custom_query,
              primary_key: draftSet.primary_key as string | undefined,
              is_streamable: draftSet.is_streamable as boolean | undefined,
              realtime_enabled: draftSet.realtime_enabled as
                | boolean
                | undefined,
              visual_query_primary_label:
                draftSet.visual_query_primary_label as string | undefined,
              visual_query_secondary_label:
                draftSet.visual_query_secondary_label as string | undefined,
            },
            approverIds:
              selectedRadioOption === saveAndRequestApproval
                ? selectedUsers
                : [],
            comment,
          });
          const errors = res.draftModelConfiguration.errors;
          if (errors && errors.length > 0) {
            throw new Error(formatBlockedColumnsError(errors));
          }
          return res.draftModelConfiguration.draft_id;
        }

        const res = await submitModel({
          ...draftInput,
          draft: draft as ModelDraft,
        });
        return res.submitDraftModel.id;
      }
    } catch (err) {
      toast({
        id: "submit-draft",
        title: "Couldn't submit this draft",
        message: err.message,
        variant: "error",
      });

      setError(err.message);
      captureException(err);
    }

    throw new Error("Invalid resource type");
  };

  const onDelete = async () => {
    const resourceId = await getResourceId();
    const func = resource === "sync" ? submitSync : submitModel;
    try {
      // clear the existing draft.
      await func({
        resourceId,
        approverIds: [],
      });
    } catch (err) {
      captureException(err);
      toast({
        id: "delete-draft",
        title: "Couldn't delete this draft",
        variant: "error",
      });

      // bubble up the error so we don't show a success toast.
      throw err;
    }

    // replace with the new resource.
    await saveResource(resourceId);
    setEditingDraft(true);
  };

  const title = () => {
    if (canApprove) {
      return "Approve & publish";
    }
    return `This ${resource} requires approval`;
  };

  // If we've already created a draft of this resource, and we started editing the
  // existing resource, then we should show a warning that we're about to delete the existing draft
  // and replace it with a new one.
  if (
    existingDraft &&
    existingDraft.operation !== DraftOperation.Create &&
    !editingDraft &&
    draftChange &&
    Object.keys(draftChange).length > 0
  ) {
    return (
      <DeleteConfirmationModal
        isOpen={open}
        resourceLabel="draft"
        content={
          <Paragraph>
            There is an existing draft on this resource. Do you want to delete
            the existing draft?
          </Paragraph>
        }
        onClose={onClose}
        onDelete={onDelete}
      />
    );
  }

  const trackAction = (resourceId) => {
    analytics.track(`Draft ${selectedRadioOption}`, {
      resource_id: resourceId,
      resource_type: resource,
      operation,
      num_requested_approvers: selectedUsers.length,
    });
  };

  const handleSave = async () => {
    setSaveLoading(true);

    const resourceId = await getResourceId();

    const isPublish = selectedRadioOption === publishNow;

    trackAction(resourceId);

    const draftId = await saveResource(resourceId);

    // If the user wants to publish these changes immediately, we apply the changes by changing the draft status to approved.
    if (isPublish) {
      if (!draftId) {
        setSaveLoading(false);
        return;
      }
      await updateDraftStatus({
        draftIds: [draftId, ...extraDraftIds],
        status: DraftStatus.Approved,
      });

      // Wait for the refetch to complete before flipping out of draft mode.
      await invalidateQueries();
      setEditingDraft(false);
    } else {
      setEditingDraft(true);
      invalidateQueries();
    }

    onSubmit(createdModelIdRef.current ?? resourceId, {
      publishNow: isPublish,
    });

    setSaveLoading(false);
    onClose();
  };

  const saveButtonText =
    selectedRadioOption === publishNow
      ? "Publish"
      : selectedRadioOption === saveOnly
        ? "Save draft"
        : "Save & request approval";

  return (
    <ChakraModal isOpen={open} onClose={onClose}>
      <ChakraModalOverlay />
      <ChakraModalContent my="auto" p={0} overflowY="auto" maxH="100%">
        <ChakraModalHeader p={6}>{title()}</ChakraModalHeader>
        <ChakraModalBody px={6} overflow="auto">
          <DraftSubmissionForm
            canApprove={canApprove}
            comment={comment}
            resource={resource}
            selectedRadioOption={selectedRadioOption}
            selectedUsers={selectedUsers}
            setComment={setComment}
            setSelectedRadioOption={setSelectedRadioOption}
            setSelectedUsers={setSelectedUsers}
            approverOptions={approverOptions}
          />
          {error && (
            <Text color="text.danger" mt={3}>
              {error}
            </Text>
          )}
        </ChakraModalBody>
        <ChakraModalFooter p={6} mt={4}>
          <Button isJustified onClick={() => onClose()}>
            Cancel
          </Button>
          <Button
            isJustified
            isDisabled={
              selectedRadioOption === saveAndRequestApproval &&
              selectedUsers.length === 0
            }
            isLoading={saveLoading}
            variant="primary"
            onClick={handleSave}
          >
            {saveButtonText}
          </Button>
        </ChakraModalFooter>
      </ChakraModalContent>
    </ChakraModal>
  );
}
